import { HttpErrorResponse } from '@angular/common/http';

import { DEALER_CHANGE_PASSWORD_COOLDOWN_STORAGE_KEY } from './single-dealer.constants';

const PASSWORD_CHANGE_LIMIT_HTTP_STATUS = 502;
const PASSWORD_CHANGE_LIMIT_ERROR = 'unable to process change password request';

/**
 * @private
 * @description Parses stored JSON into a dealer user id to cooldown-end map, or an empty object when invalid.
 * @param raw - JSON string from sessionStorage or null
 * @returns Map of dealer Auth user id to cooldown end timestamp (epoch ms)
 */
function parseCooldownMapJson(raw: string | null): Record<string, number> {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed as Record<string, number>;
    } catch {
        return {};
    }
}

/**
 * @public
 * @description Reads the per-dealer change-password cooldown map, drops expired entries, and persists when the map shrinks.
 * @param storage - Browser sessionStorage (or compatible Storage API)
 * @returns Active cooldown entries keyed by dealer Auth user id
 */
export function readDealerChangePasswordCooldownMap(storage: Storage): Record<string, number> {
    const m = parseCooldownMapJson(storage.getItem(DEALER_CHANGE_PASSWORD_COOLDOWN_STORAGE_KEY));
    const now = Date.now();
    const pruned: Record<string, number> = {};
    for (const [k, v] of Object.entries(m)) {
        if (typeof v === 'number' && v > now) pruned[k] = v;
    }
    if (Object.keys(pruned).length !== Object.keys(m).length) {
        try {
            storage.setItem(DEALER_CHANGE_PASSWORD_COOLDOWN_STORAGE_KEY, JSON.stringify(pruned));
        } catch {
            /* quota */
        }
    }
    return pruned;
}

/**
 * @public
 * @description Persists the full cooldown map to sessionStorage.
 * @param storage - Browser sessionStorage (or compatible Storage API)
 * @param map - Dealer user id to cooldown end timestamp (epoch ms)
 * @returns void
 */
export function writeDealerChangePasswordCooldownMap(
    storage: Storage,
    map: Record<string, number>,
): void {
    try {
        storage.setItem(DEALER_CHANGE_PASSWORD_COOLDOWN_STORAGE_KEY, JSON.stringify(map));
    } catch {
        /* quota */
    }
}

/**
 * @public
 * @description Computes whole seconds remaining until the cooldown expires for one dealer.
 * @param storage - Browser sessionStorage (or compatible Storage API)
 * @param userId - Dealer Auth user id
 * @returns Seconds left on the cooldown, or 0 if none or expired
 */
export function dealerChangePasswordCooldownSecondsLeft(storage: Storage, userId: string): number {
    if (!userId) return 0;
    const until = readDealerChangePasswordCooldownMap(storage)[userId];
    if (!until) return 0;
    const now = Date.now();
    if (until <= now) return 0;
    return Math.ceil((until - now) / 1000);
}

/**
 * @public
 * @description Records a change-password cooldown for a dealer after a successful send.
 * @param storage - Browser sessionStorage (or compatible Storage API)
 * @param userId - Dealer Auth user id
 * @param ttlSeconds - Cooldown length from the API in seconds
 * @returns void
 */
export function setDealerChangePasswordCooldown(
    storage: Storage,
    userId: string,
    ttlSeconds: number,
): void {
    if (!userId || ttlSeconds <= 0) return;
    const map = readDealerChangePasswordCooldownMap(storage);
    map[userId] = Date.now() + ttlSeconds * 1000;
    writeDealerChangePasswordCooldownMap(storage, map);
}

/**
 * @public
 * @description Removes a dealer's cooldown entry from storage (e.g. after the timer completes).
 * @param storage - Browser sessionStorage (or compatible Storage API)
 * @param userId - Dealer Auth user id
 * @returns void
 */
export function clearDealerChangePasswordCooldown(storage: Storage, userId: string): void {
    if (!userId) return;
    const map = readDealerChangePasswordCooldownMap(storage);
    if (!(userId in map)) return;
    delete map[userId];
    writeDealerChangePasswordCooldownMap(storage, map);
}

/**
 * @public
 * @description Formats a duration in seconds for UI countdown labels (`X mins Y secs`, or `Y secs` under a minute).
 * @param seconds - Total seconds to format
 * @returns Formatted string (e.g. "5 mins 0 secs", "1 min 5 secs", "56 secs"), or empty when seconds is not positive
 */
export function formatCooldownTime(seconds: number): string {
    if (seconds <= 0) return '';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const minPart = minutes === 0 ? '' : `${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
    const secPart = `${secs} ${secs === 1 ? 'sec' : 'secs'}`;
    return minPart ? `${minPart} ${secPart}` : secPart;
}

/**
 * @public
 * @description Coerces a value to a trimmed string for safe comparisons and display.
 * @param value - Arbitrary input (often form or API field)
 * @returns Trimmed string, or empty string for nullish input
 */
export function normalizeString(value: unknown): string {
    return String(value ?? '').trim();
}

/**
 * @public
 * @description Strips non-digits from a phone string while preserving a leading plus for tel: URIs.
 * @param phone - Raw phone string
 * @returns Compact string suitable for tel: links
 */
export function getCompactPhone(phone: string): string {
    const compact = phone.replace(/[^\d+]/g, '');
    return compact.length > 0 ? compact : phone;
}

/**
 * @public
 * @description True when `POST /api/users/:id/force-password-change` failed due to rate limiting.
 */
export function isDealerPasswordChangeLimitError(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse) || err.status !== PASSWORD_CHANGE_LIMIT_HTTP_STATUS) {
        return false;
    }
    const errors = (err.error as { errors?: unknown } | undefined)?.errors;
    if (!Array.isArray(errors)) {
        return false;
    }
    return errors.some(
        (message) =>
            typeof message === 'string' &&
            message.trim().toLowerCase().includes(PASSWORD_CHANGE_LIMIT_ERROR),
    );
}
